import React, { useRef, useState, useEffect } from "react";
import Chart from "react-apexcharts";
import { Box } from "@mui/material";
import ApexCharts from 'apexcharts';
import CODE from "../../../../Static/Constants/StatusCodes";
import { ThemeProperties } from "../../../../Theme/ThemeProperties";
import { formatUTCCalendarDate, parseCalendarDateToUTC } from "../../../../Helper/DatePicker/DateConverter";
import CartLoader from "../../../../Helper/Loaders/CartLoader";
import { isBaseDataSeriesEmpty } from "../../../../Helper/DigitalTwin/VitalChartConfigurations";
import ZoomResetView from "../../components/ZoomResetView";
import NoDataOnChart from "../../components/NoDataOnChart";
import ChartActions from "../BatteryVitals/ChartActions";
import DownloadCSV from "../../../../Helper/CSVOperations/DownloadCSV";
import { toTitleCase } from "../../../../Helper/Formatters/TextFormatter";
import { formatKmAxisTick, KM_X_AXIS_KEYS } from "../../../../Helper/Formatters/NumberFormatter";
import { computeNiceAxis } from "../../../BatteryIntelligence/SOHAndRUL/utils/niceScale";
import ReactDOMServer from "react-dom/server";
import { setupAnnotationTooltips } from "../../../AdvancedAnalytics/Common/CommonFunctions";
import PackReplacementTooltip from "./PackReplacementTooltip";
import { filterTimePointsInRange, getPackReplacementMarkerUrl } from "./packReplacementAnnotations";
import { DEFAULT_SERIES_COLORS, getSeriesColor } from "./seriesColors";

// x-axis dimension that plots each point at its SOH computation date (datetime axis)
export const SOH_COMPUTED_DATE_X_KEY = "sohComputedDate";

// Series that can be drawn on the secondary (right) y-axis, keyed by the org-meta
// `axisCombinations[].y2` entry. Each is read off the SOH points of one entity.
const Y2_SERIES = {
    odometer: {
        label: "Odometer (km)",
        color: "#5F6B7A",
        getValue: (point) => point.odometerKm,
        formatAxis: (value) => formatKmAxisTick(value),
        formatTooltip: (value) => `${Math.round(value).toLocaleString("en-IN")} km`,
    },
};
const Y2_DASH = 6;

// The battery system line is drawn thicker than its strings / packs so it stands out.
const SERIES_STROKE_WIDTH = 2.5;
const BATTERY_SERIES_STROKE_WIDTH = 4;

// One stroke width per plotted series: the SOH entities in seriesList order, then
// y2Count secondary (dashed) series, which keep the default width. An empty chart
// still gets one width.
export const getSeriesStrokeWidths = (seriesList, y2Count = 0) => {
    const widths = [
        ...(seriesList || []).map((e) => (e.entityType === "battery" ? BATTERY_SERIES_STROKE_WIDTH : SERIES_STROKE_WIDTH)),
        ...Array(y2Count).fill(SERIES_STROKE_WIDTH),
    ];
    return widths.length ? widths : [SERIES_STROKE_WIDTH];
};

// Share of the date range left empty on each side, so a marker on the first or last
// day is not drawn half over the y-axis.
const DATE_AXIS_PADDING = 0.02;
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

// Calendar date of a SOH / forecast point, on the same UTC day the axis shows.
const formatPointDate = (date) => {
    const ts = parseCalendarDateToUTC(date);
    return ts === null ? "Unknown" : formatUTCCalendarDate(ts);
};

/**
 * CSV columns for the configured x-axis dimensions, plus the secondary-axis odometer.
 * Only dimensions in org-meta axisCombinations are exported (so Age / Eq. Cycle are
 * omitted when not configured); the date dimension is already the "Date" column.
 */
export const buildDimensionColumns = (item, isRul, { xAxisDimensions = [], cycleAgeTypeText, y2Keys = [] }) => {
    const cols = {};
    xAxisDimensions.forEach((dim) => {
        if (dim === 'addedOn') {
            const days = isRul ? item.calendarAgeInDays : item.age;
            cols["Age (months)"] = days !== null && days !== undefined ? (days / 30).toFixed(2) : "";
        } else if (dim === 'cycleCount') {
            cols[cycleAgeTypeText] = item.eqCycleEst?.toFixed(2);
        } else if (dim === 'odometer' || dim === 'latestSOHPredictionOdometer') {
            cols["Odometer (km)"] = parseFloat(item?.distanceTravelledKm ?? 0).toFixed(0);
        }
    });
    // Secondary-axis odometer; forecast (RUL) rows carry no odometer, so they stay blank
    if (y2Keys.includes('odometer') && !("Odometer (km)" in cols)) {
        const km = parseFloat(item?.distanceTravelledKm);
        cols["Odometer (km)"] = !isRul && !isNaN(km) ? km.toFixed(0) : "";
    }
    return cols;
};

const DEFAULT_TOOLTIP_FIELDS = [
    { key: 'soh', label: 'SOH' },
    { key: 'age', label: 'Age (Months)' },
    { key: 'cycleCount', label: 'BMS Cycles' },
];

// Returns the value string for a given tooltipField key from a data point
const getTooltipFieldValue = (key, point) => {
    if (key === 'soh') return Number.isFinite(point.y) ? `${parseInt(point.y)} %` : 'N/A';
    if (key === 'odometer' || key === 'latestSOHPredictionOdometer') return formatKmAxisTick(point.distanceTravelledKm || 0);
    if (key === 'cycleCount') return `${(point.eqCycleEst || 0).toFixed(0)}`;
    if (key === 'age') return `${(point.ageMonths || 0).toFixed(1)}`;
    if (key === 'sohConfidence') return point.sohConfidence ? toTitleCase(point.sohConfidence) : 'N/A';
    return 'N/A';
};

// Finds the data point in a series whose x is closest to targetX
const findClosestPoint = (points, targetX) => {
    if (!points?.length) return null;
    return points.reduce((closest, point) => {
        if (!point) return closest;
        if (!closest) return point;
        return Math.abs((point.x || 0) - targetX) < Math.abs((closest.x || 0) - targetX)
            ? point
            : closest;
    }, null);
};

const BatteryHealthChartV2 = ({
    data,
    cycleAgeTypeText,
    tooltipFields,
    deviceID,
    selectedXAxisTitle,
    xAxisKey,
    xAxisDimensions = [],
    searchID,
    searchColumn,
    y2Keys = [],
    replacementTimePoints = [],
    seriesColors,
}) => {
    const [showXTitle, setShowXTitle] = useState(false);
    const zoomResetViewRef = useRef(null);
    const replacementTooltipCleanupRef = useRef(null);
    // Remove the body-mounted replacement cards when the chart goes away.
    useEffect(() => () => replacementTooltipCleanupRef.current?.(), []);
    const chartID = `battery-health-chart-v2-${deviceID}`;
    const statusCode = data?.responseStatus?.code;
    const seriesList = data?.seriesList || [];

    // km dimensions (odometer / latestSOHPredictionOdometer) get "k km" scaled ticks
    const isKmAxis = KM_X_AXIS_KEYS.has(xAxisKey);
    const isDateAxis = xAxisKey === SOH_COMPUTED_DATE_X_KEY;
    const replacementPoints = isDateAxis ? replacementTimePoints || [] : [];
    const activeY2Keys = (y2Keys || []).filter((key) => Y2_SERIES[key]);
    const getY2Label = (key) => tooltipFields?.find((f) => f.key === key)?.label || Y2_SERIES[key].label;

    // Clickable legend: show/hide each series. Reset visibility when the series set changes.
    const seriesNamesKey = seriesList.map((e) => e.label).join('|');
    const [hiddenSeries, setHiddenSeries] = useState({});
    // Reset visibility when the series set OR the x-axis dimension changes, so the
    // legend's hidden state can't desync from the chart after a refetch.
    useEffect(() => { setHiddenSeries({}); }, [seriesNamesKey, xAxisKey]);
    const toggleSeries = (name) => {
        const willHide = !hiddenSeries[name];
        ApexCharts.exec(chartID, willHide ? 'hideSeries' : 'showSeries', name);
        setHiddenSeries((prev) => ({ ...prev, [name]: willHide }));
    };

    if (statusCode === CODE.LOADING) {
        return (
            <Box sx={{ minHeight: '360px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <CartLoader />
            </Box>
        );
    }

    const getChartSettings = (isChartEmpty) => ({
        id: chartID,
        zoom: {
            enabled: !isChartEmpty,
            type: 'x',
            allowMouseWheelZoom: false,
        },
        toolbar: { show: false },
        animations: { enabled: false },
        events: {
            zoomed: () => {
                zoomResetViewRef?.current?.show();
            },
            mounted: (chartContext) => setupReplacementTooltips(chartContext),
            updated: (chartContext) => setupReplacementTooltips(chartContext),
        },
    });

    // Hover / click-to-pin cards for the replacement markers, via the same helper the
    // alert annotations use. It pairs marker images with time points by position, so
    // pass only the points ApexCharts drew (it clips anything outside the zoom window).
    const setupReplacementTooltips = (chartContext) => {
        replacementTooltipCleanupRef.current?.();
        replacementTooltipCleanupRef.current = null;
        if (!replacementPoints.length) return;
        const { minX, maxX } = chartContext?.w?.globals || {};
        replacementTooltipCleanupRef.current = setupAnnotationTooltips(
            chartContext,
            replacementPoints,
            (points) => filterTimePointsInRange(points, minX, maxX),
            (timePoint) => ReactDOMServer.renderToStaticMarkup(<PackReplacementTooltip timePoint={timePoint} />)
        );
    };

    // One marker per replacement day on top of the SOH axis, with a vertical guide
    // line, styled like the Smart Alert / BMS fault markers.
    const getReplacementAnnotations = () => {
        if (!replacementPoints.length || statusCode !== CODE.SUCCESS) return { xaxis: [], points: [] };
        const sohAxisMax = getPrimaryYAxisSettings().max;
        return {
            xaxis: replacementPoints.map(({ timestamp }) => ({
                x: timestamp,
                borderColor: "#BBBBBB",
                strokeDashArray: 0,
            })),
            points: replacementPoints.map(({ timestamp, count }) => ({
                x: timestamp,
                y: sohAxisMax,
                yAxisIndex: 0,
                offsetY: 10,
                marker: { size: 0 },
                image: {
                    path: getPackReplacementMarkerUrl(count),
                    width: 25,
                    height: 25,
                    offsetX: 0,
                    offsetY: 0,
                },
            })),
        };
    };

    const getPrimaryYAxisSettings = () => ({
        min: 60,
        max: 110,
        tickAmount: 6,
        axisBorder: { show: true, color: "#808080", offsetX: -1.5 },
        labels: {
            style: {
                colors: "#87939D",
                fontSize: ThemeProperties.c_axis_labels,
                fontFamily: "Roboto",
                fontWeight: ThemeProperties.c_axis_labels_weight,
            },
            formatter: (value) => Math.round(value),
        },
        title: {
            text: "State of Health (%)",
            offsetX: 5,
            style: {
                color: '#87939D',
                fontSize: ThemeProperties.c_axis_title,
                fontWeight: ThemeProperties.c_axis_labels_weight,
                fontFamily: "Roboto",
            },
        },
    });

    const getSecondaryYAxisSettings = (key, values) => {
        const scale = computeNiceAxis(values.length ? values : [0], 5);
        return {
            seriesName: [getY2Label(key)],
            opposite: true,
            min: scale.min,
            max: scale.max,
            tickAmount: scale.tickCount,
            axisBorder: { show: true, color: "#808080" },
            labels: {
                style: {
                    colors: "#87939D",
                    fontSize: ThemeProperties.c_axis_labels,
                    fontFamily: "Roboto",
                    fontWeight: ThemeProperties.c_axis_labels_weight,
                },
                formatter: (value) => (Number.isFinite(value) ? Y2_SERIES[key].formatAxis(value) : ""),
            },
            title: {
                text: getY2Label(key),
                style: {
                    color: '#87939D',
                    fontSize: ThemeProperties.c_axis_title,
                    fontWeight: ThemeProperties.c_axis_labels_weight,
                    fontFamily: "Roboto",
                },
            },
        };
    };

    // One axis per side: every SOH series maps to the left axis by name, each y2
    // series to its own right axis. A plain object is kept when there is no y2.
    const getYAxisSettings = (sohSeriesNames, y2Series) => {
        if (!y2Series.length) return getPrimaryYAxisSettings();
        return [
            { ...getPrimaryYAxisSettings(), seriesName: sohSeriesNames },
            ...y2Series.map((s) => getSecondaryYAxisSettings(s.y2Key, s.data.map((p) => p.y))),
        ];
    };

    const getXAxisSettings = (xScale) => isDateAxis ? {
        type: "datetime",
        // Widen the range so a replacement before the first or after the last SOH
        // computation still gets a marker instead of being clipped.
        ...(replacementPoints.length && dateRange ? { min: dateRange.min, max: dateRange.max } : {}),
        tooltip: { enabled: false },
        axisBorder: { show: true, color: ThemeProperties.c_axis_labels_color },
        axisTicks: { show: false },
        labels: {
            rotate: 0,
            // sohML.date is a calendar DATE serialised as UTC midnight; read it in UTC so
            // the label never shifts a day for browsers west of UTC.
            datetimeUTC: true,
            hideOverlappingLabels: true,
            style: { colors: "#87939D", fontSize: "12px", fontFamily: "Roboto", fontWeight: "500" },
            offsetY: -2,
            // Unit follows the zoom level (year -> month -> day); no custom formatter, or
            // every tick in a zoomed-in window reads the same month.
            datetimeFormatter: { year: "yyyy", month: "MMM 'yy", day: "dd MMM", hour: "dd MMM" },
        },
        title: {
            text: showXTitle ? selectedXAxisTitle : undefined,
            offsetX: 3,
            offsetY: -5,
            style: {
                color: ThemeProperties.black,
                fontSize: ThemeProperties.c_axis_title,
                fontFamily: "Roboto",
                fontWeight: ThemeProperties.c_axis_title_weight,
            },
        },
    } : {
        type: "numeric",
        tooltip: { enabled: false },
        tickAmount: xScale.tickCount,
        tickPlacement: 'on',
        // Start at the data's nice lower bound (not forced to 0) so series that
        // begin at a high odometer don't leave a large empty gap on the left.
        min: xScale.min,
        max: xScale.max,
        axisBorder: {
            show: true,
            color: ThemeProperties.c_axis_labels_color,
        },
        labels: {
            rotate: 0,
            datetimeUTC: false,
            rotateAlways: false,
            hideOverlappingLabels: true,
            style: {
                colors: "#87939D",
                fontSize: "12px",
                fontFamily: "Roboto",
                fontWeight: "500",
                LineHeight: "16px",
            },
            offsetX: 0,
            offsetY: -2,
            formatter: (value) => {
                if (typeof value !== "number") return value ?? "";
                return isKmAxis ? formatKmAxisTick(value) : value.toFixed(0);
            },
        },
        title: {
            text: showXTitle ? selectedXAxisTitle : undefined,
            offsetX: 3,
            offsetY: -5,
            style: {
                color: ThemeProperties.black,
                fontSize: ThemeProperties.c_axis_title,
                fontFamily: "Roboto",
                fontWeight: ThemeProperties.c_axis_title_weight,
            },
        },
        axisTicks: {
            show: false,
            height: 3,
            borderType: "solid",
            color: ThemeProperties.c_axis_labels_color,
        },
    };

    const getTooltipSettings = (isChartEmpty) => {
        const fields = tooltipFields?.length > 0 ? tooltipFields : DEFAULT_TOOLTIP_FIELDS;

        const formatTooltip = ({ seriesIndex, dataPointIndex, w }) => {
            const hoveredPoint = w.globals.initialSeries[seriesIndex]?.data?.[dataPointIndex];
            if (!hoveredPoint) return '';

            const date = formatPointDate(hoveredPoint.date);
            const hoveredX = hoveredPoint.x;

            const seriesRows = w.globals.initialSeries.map((s, i) => {
                // Series hidden from the legend stay in initialSeries; leave them out.
                if (w.globals.collapsedSeriesIndices?.includes(i)) return '';
                const point = findClosestPoint(s.data, hoveredX);
                if (!point || !Number.isFinite(point.y)) return '';

                const color = w.globals.colors[i] || '#4D44D8';
                const name = w.globals.seriesNames[i];
                const isForecasted = !!point.isForecasted;

                if (point.y2Key) {
                    return `<div style="display:flex; align-items:center; gap:6px; margin-bottom: 10px;">
                        <span style="width:14px; border-top:2px dashed ${color}; flex-shrink:0;"></span>
                        <span style="font-weight:500; white-space:nowrap;">${name}:</span>
                        <strong>${Y2_SERIES[point.y2Key].formatTooltip(point.y)}</strong>
                    </div>`;
                }

                // The odometer has its own row when it is drawn on the secondary axis.
                const visibleFields = fields.filter((f) =>
                    !(f.key === 'odometer' && activeY2Keys.includes('odometer'))
                );

                const fieldRows = visibleFields.map(({ key, label }) => {
                    const displayLabel = isForecasted && key === 'soh' ? `Predicted ${label}` : label;
                    const value = getTooltipFieldValue(key, point);
                    return `<div style="display:flex; margin-bottom: 4px; gap: 6px;">
                        <span style="color: #8F8F8F; font-weight: 500;">${displayLabel}:</span>
                        <strong>${value}</strong>
                    </div>`;
                }).join('');

                return `<div style="display:flex; margin-bottom: 10px;">
                    <div style="display:flex; align-items:flex-start; gap:6px; padding-top:2px; flex-shrink:0;">
                        <span class="dot" style="background-color:${color}; margin-top:4px; flex-shrink:0;"></span>
                        <span style="font-weight:500; white-space:nowrap;">${name}</span>
                        <span>:</span>
                    </div>
                    <div style="padding-left:8px;">${fieldRows}</div>
                </div>`;
            }).filter(Boolean).join('');

            return `<div>
                <div class="apexcharts-tooltip-title">${date}</div>
                <div class="tooltip_container" style="font-size:12px;">${seriesRows}</div>
            </div>`;
        };

        return {
            enabled: !isChartEmpty,
            intersect: false,
            followCursor: true,
            fixed: { enabled: false },
            custom: formatTooltip,
        };
    };

    const generateSeries = () => {
        if (statusCode !== CODE.SUCCESS) return [];
        return seriesList.map((entry) => {
            const sohPoints = (entry.sohData || []).map(item => ({ ...item, isForecasted: false, entityType: entry.entityType }));
            const rulPoints = (entry.rulData || []).map(item => ({ ...item, isForecasted: true, entityType: entry.entityType }));
            return {
                type: "line",
                name: entry.label,
                data: [...sohPoints, ...rulPoints],
            };
        });
    };

    const sohSeries = generateSeries();

    // Secondary series come from one entity: the battery-level series when it is
    // selected (vehicle odometer), otherwise the first selected entity with data.
    const y2Source = seriesList.find((e) => e.entityType === 'battery' && e.sohData?.length)
        || seriesList.find((e) => e.sohData?.length);
    const y2Series = statusCode === CODE.SUCCESS && y2Source
        ? activeY2Keys.map((key) => ({
            type: "line",
            name: getY2Label(key),
            y2Key: key,
            data: y2Source.sohData
                .map((p) => ({ x: p.x, y: Y2_SERIES[key].getValue(p), date: p.date, y2Key: key }))
                .filter((p) => Number.isFinite(p.y)),
        })).filter((s) => s.data.length)
        : [];
    const series = [...sohSeries, ...y2Series];

    // Nice axis bounds computed from the actual x data: floors the lower bound to
    // the data's min (rounded down to a nice step) so the axis starts where the
    // data starts instead of always at 0.
    const dateSeriesX = isDateAxis
        ? series.flatMap((s) => s.data.map((p) => p.x)).filter((v) => Number.isFinite(v))
        : [];
    const dateRangeValues = [...dateSeriesX, ...replacementPoints.map((p) => p.timestamp)];
    const dateRange = (() => {
        if (!dateRangeValues.length) return null;
        const min = Math.min(...dateRangeValues);
        const max = Math.max(...dateRangeValues);
        const pad = Math.max((max - min) * DATE_AXIS_PADDING, ONE_DAY_MS);
        return { min: min - pad, max: max + pad };
    })();

    const allXValues = isDateAxis ? [] : seriesList.flatMap((entry) =>
        [...(entry.sohData || []), ...(entry.rulData || [])]
            .map((item) => item.x)
            .filter((v) => typeof v === "number" && isFinite(v))
    );
    const xScale = computeNiceAxis(allXValues.length ? allXValues : [0], 6);

    const isChartEmpty = statusCode !== CODE.SUCCESS;
    const seriesCount = Math.max(series.length, 1);
    const colors = seriesList.length > 0
        ? [
            // Fixed per entity from its level + position; org meta palette overrides the defaults
            ...seriesList.map((e, i) => getSeriesColor(e.colorKey || { level: e.entityType, position: i + 1 }, seriesColors)),
            ...y2Series.map((s) => Y2_SERIES[s.y2Key].color),
        ]
        : [DEFAULT_SERIES_COLORS.battery[0]];

    const options = {
        colors,
        dataLabels: { enabled: false },
        fill: { opacity: Array(seriesCount).fill(1) },
        forecastDataPoints: { count: 0 },
        stroke: {
            curve: "straight",
            width: getSeriesStrokeWidths(seriesList, y2Series.length),
            dashArray: series.map((s) => (s.y2Key ? Y2_DASH : 0)),
        },
        markers: { size: 0, hover: { size: 5, sizeOffset: 1 } },
        legend: {
            show: false,
        },
        chart: getChartSettings(isChartEmpty),
        yaxis: getYAxisSettings(sohSeries.map((s) => s.name), y2Series),
        xaxis: getXAxisSettings(xScale),
        tooltip: getTooltipSettings(isChartEmpty),
        annotations: getReplacementAnnotations(),
    };

    const noDataMessage = statusCode === "EMPTY_SELECTION"
        ? "Please select at least one item"
        : "SOHc for this battery is not computed";

    const dimensionOptions = { xAxisDimensions, cycleAgeTypeText, y2Keys: activeY2Keys };

    const getCSVDataForExport = () => {
        const rows = [];
        for (const entry of seriesList) {
            (entry.sohActualResponse || []).forEach(item => rows.push({
                "Label": entry.label,
                "Serial Number": deviceID,
                ...buildDimensionColumns(item, false, dimensionOptions),
                "SOH (%)": item.sohEst?.toFixed(2),
                "SOH Estimation Confidence": item.sohConfidence ? toTitleCase(item.sohConfidence) : '',
                "Initial Capacity (Ah)": parseFloat(item?.initialCapacity ?? 0).toFixed(2),
                "Present Capacity (Ah)": parseFloat(item?.presentCapacity ?? 0).toFixed(2),
                "Date": formatPointDate(item.date),
                "Type": "SOH",
            }));
            (entry.rulActualResponse || []).forEach(item => rows.push({
                "Label": entry.label,
                "Serial Number": deviceID,
                ...buildDimensionColumns(item, true, dimensionOptions),
                "SOH (%)": item.sohForecast?.toFixed(2),
                // Forecast (RUL) rows have no confidence; keep the column for a consistent header
                "SOH Estimation Confidence": '',
                "Initial Capacity (Ah)": parseFloat(item?.initialCapacity ?? 0).toFixed(2),
                "Present Capacity (Ah)": parseFloat(item?.presentCapacity ?? 0).toFixed(2),
                "Date": formatPointDate(item.date),
                "Type": "RUL",
            }));
        }
        return rows;
    };

    const downloadChartAsCSV = async () => {
        try {
            DownloadCSV(`Battery Health ${deviceID}`, getCSVDataForExport());
        } catch (err) {
            console.error(err);
        }
    };

    const downloadChartAsSVG = async () => {
        try {
            setShowXTitle(true);
            setTimeout(() => {
                const chartInstance = ApexCharts.getChartByID(chartID);
                chartInstance.exports.exportToSVG();
                setShowXTitle(false);
            }, 10);
        } catch (error) {
            console.error('Error downloading chart:', error);
        }
    };

    const legendItems = series.map((s, i) => ({
        name: s.name,
        color: colors[i % colors.length],
        isDashed: !!s.y2Key,
    }));

    return (
        <Box sx={{ position: 'relative' }}>
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '12px',
                pl: '12px',
                pr: '40px',
                pt: '10px',
                minHeight: '32px',
            }}>
                {legendItems.map((item) => {
                    const isHidden = !!hiddenSeries[item.name];
                    return (
                        <Box
                            key={item.name}
                            onClick={() => toggleSeries(item.name)}
                            sx={{
                                display: 'flex', alignItems: 'center', gap: '6px',
                                cursor: 'pointer', userSelect: 'none',
                                opacity: isHidden ? 0.4 : 1, transition: 'opacity 0.15s',
                            }}
                        >
                            {item.isDashed
                                ? <Box sx={{ width: 16, borderTop: `2px dashed ${item.color}`, flexShrink: 0 }} />
                                : <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: item.color, flexShrink: 0 }} />}
                            <Box sx={{ fontFamily: 'Roboto', fontSize: '12px', fontWeight: 400, color: '#3D4B5A', lineHeight: 1, textDecoration: isHidden ? 'line-through' : 'none' }}>
                                {item.name}
                            </Box>
                        </Box>
                    );
                })}

                <Box sx={{ position: 'absolute', right: 0, top: '6px', zIndex: 1, display: 'flex', alignItems: 'center' }}>
                    <ZoomResetView ref={zoomResetViewRef} chartID={chartID} />
                    <ChartActions actions={[
                        { type: 'nothing', label: 'Download CSV', icon: 'download-file-icon', onClick: downloadChartAsCSV },
                        { type: 'nothing', label: 'Download Image', icon: 'download-image-icon', onClick: downloadChartAsSVG },
                    ]} />
                </Box>
            </Box>

            <Box sx={{ position: 'relative' }}>
                <NoDataOnChart
                    onClick={() => {}}
                    isDataEmpty={isBaseDataSeriesEmpty(series)}
                    disableRedirect={true}
                    noDataMessage={noDataMessage}
                />
                <Chart
                    key={chartID}
                    options={options}
                    series={series}
                    height={330}
                    type={"line"}
                    width={'100%'}
                />
            </Box>
        </Box>
    );
};

export default BatteryHealthChartV2;
